import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import { Avatar, Box, Button, Card, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api, unwrap, type Schemas } from '../../api/client';
import { EmptyState, ErrorState, LoadingRows } from '../../components/states';
import { date, dateTime, initials, plural, relativeTime } from '../../lib/format';

type Entry = Schemas['TenantTimelineEntry'];
type Changes = Record<string, unknown> | null;

const PAGE = 30;

export const timelineKey = (tenantId: string) =>
  ['platform', 'tenants', 'timeline', tenantId] as const;

const str = (v: unknown) => (typeof v === 'string' ? v : null);
const strings = (v: unknown) =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
/** `fast_sale` → `Fast sale`; the timeline has no menu catalog of its own. */
const menuLabel = (key: string) => {
  const s = key.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const num = (v: unknown) => (typeof v === 'number' ? v : null);
const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

function paidRange(c: Changes): string | null {
  const before = str(c?.paidThroughBefore);
  const after = str(c?.paidThroughAfter);
  if (!after) return null;
  return before && before !== after
    ? `Paid through ${date(before)} → ${date(after)}`
    : `Paid through ${date(after)}`;
}

/** Plain-language title and details for one entry. */
export function describe(e: Entry): { title: string; details: (string | null)[]; tone: Tone } {
  const c = obj(e.changes);
  const days = num(c?.daysAdded);
  switch (e.action) {
    case 'tenant.register':
      return { title: 'Shop registered', details: [str(c?.name), str(c?.slug)], tone: 'brand' };
    case 'subscription.created':
      return {
        title: days ? `Free trial started (${plural(days, 'day')})` : 'Free trial started',
        details: [paidRange(c)],
        tone: 'brand',
      };
    case 'subscription.code_redeemed':
      return {
        title: days
          ? `Activation code redeemed (+${plural(days, 'day')})`
          : 'Activation code redeemed',
        details: [paidRange(c)],
        tone: 'success',
      };
    case 'subscription.extend': {
      const renewed = str(c?.kind) === 'RENEWED';
      return {
        title: renewed
          ? `Paid subscription ${c?.startToday ? 'started today' : 'renewed'}${days ? ` (+${plural(days, 'day')})` : ''}`
          : `Extended by ${days ? plural(days, 'day') : 'some days'}`,
        details: [paidRange(c), str(c?.paymentRef) ? `Payment ref ${String(c?.paymentRef)}` : null],
        tone: renewed ? 'success' : 'info',
      };
    }
    case 'subscription.correct': {
      const before = obj(c?.before) ?? {};
      const after = obj(c?.after) ?? {};
      const was = num(before.days);
      const now = num(after.days);
      return {
        title:
          was !== null && now !== null && was !== now
            ? `Entry corrected: ${plural(was, 'day')} → ${plural(now, 'day')}`
            : 'Entry corrected',
        details: [
          paidRange({ paidThroughBefore: before.paidThrough, paidThroughAfter: after.paidThrough }),
          str(c?.reason) ? `Reason: ${String(c?.reason)}` : null,
        ],
        tone: 'warning',
      };
    }
    case 'subscription.reverse':
      return {
        title: str(c?.kind) === 'RENEWED' ? 'Renewal deleted' : 'Extension deleted',
        details: [paidRange(c), str(c?.reason) ? `Reason: ${String(c?.reason)}` : null],
        tone: 'error',
      };
    case 'subscription.update': {
      const after = obj(c?.after) ?? {};
      const parts = [
        'planId' in after ? (after.planId ? 'Plan changed' : 'Plan removed') : null,
        num(after.graceDays) !== null
          ? `Grace period ${plural(Number(after.graceDays), 'day')}`
          : null,
        'maxUsersOverride' in after
          ? after.maxUsersOverride === null
            ? 'User limit back to the plan’s'
            : `User limit set to ${plural(Number(after.maxUsersOverride), 'user')}`
          : null,
      ].filter((p): p is string => p !== null);
      return { title: parts[0] ?? 'Subscription edited', details: parts.slice(1), tone: 'info' };
    }
    case 'tenant.menus': {
      const was = new Set(strings(obj(c?.before)?.hiddenMenus));
      const now = new Set(strings(obj(c?.after)?.hiddenMenus));
      const off = [...now].filter((k) => !was.has(k));
      const on = [...was].filter((k) => !now.has(k));
      return {
        title: 'Menus changed',
        details: [
          off.length ? `Turned off: ${off.map(menuLabel).join(', ')}` : null,
          on.length ? `Turned on: ${on.map(menuLabel).join(', ')}` : null,
        ],
        tone: 'info',
      };
    }
    case 'tenant.backup': {
      const files = obj(c?.after)?.includesFiles;
      return {
        title: 'Backup downloaded',
        details: [files ? 'With files' : 'Data only'],
        tone: 'info',
      };
    }
    case 'tenant.user_password_reset':
      return { title: 'A user’s password was reset', details: [], tone: 'warning' };
    case 'tenant.suspend':
      return { title: 'Shop suspended', details: [str(c?.reason)], tone: 'error' };
    case 'tenant.resume':
      return { title: 'Shop resumed', details: [str(c?.reason)], tone: 'success' };
    case 'activation_code.generate':
      return {
        title: 'Activation code created',
        details: [
          num(c?.durationDays) ? plural(Number(c?.durationDays), 'day') : null,
          str(c?.expiresAt) ? `valid until ${date(String(c?.expiresAt))}` : null,
        ],
        tone: 'info',
      };
    case 'activation_code.revoke':
      return { title: 'Activation code revoked', details: [str(c?.reason)], tone: 'warning' };
    case 'activation_code.deliver':
      return {
        title: 'Activation code sent',
        details: [str(c?.channel) ? `via ${String(c?.channel).replace('_', ' ')}` : null],
        tone: 'info',
      };
    case 'support.access':
      return { title: 'Support viewed the shop’s data', details: [], tone: 'warning' };
    case 'support_access.grant':
      return {
        title: 'Support access granted',
        details: [
          str(c?.expiresAt) ? `until ${dateTime(String(c?.expiresAt))}` : null,
          str(c?.note),
        ],
        tone: 'warning',
      };
    case 'support_access.revoke':
      return { title: 'Support access withdrawn', details: [], tone: 'info' };
    case 'tenant.create':
      return {
        title: 'Shop created for the customer',
        details: [
          str(c?.name),
          str(c?.ownerName) ? `Owner ${String(c?.ownerName)}` : null,
          str(c?.ownerEmail) ?? str(c?.ownerPhone),
        ],
        tone: 'brand',
      };
    case 'settings.update':
      return { title: 'Shop settings changed', details: changeLines(c), tone: 'info' };
    case 'settings.logo':
      return { title: 'Shop logo changed', details: [], tone: 'info' };
    case 'numbering.update':
      return { title: 'Document numbering changed', details: changeLines(c), tone: 'info' };
    case 'tax_rate.create':
      return { title: 'Tax rate added', details: changeLines(c), tone: 'info' };
    case 'tax_rate.update':
      return { title: 'Tax rate changed', details: changeLines(c), tone: 'info' };
    case 'role.create':
      return { title: 'Role created', details: changeLines(c), tone: 'info' };
    case 'role.update':
      return { title: 'Role changed', details: changeLines(c), tone: 'info' };
    case 'role.delete':
      return { title: 'Role deleted', details: changeLines(c), tone: 'warning' };
    case 'staff.create':
      return {
        title: `Added user ${e.subject ?? ''}`.trim(),
        details: changeLines(c),
        tone: 'info',
      };
    case 'staff.update':
      return {
        title: `Changed user ${e.subject ?? ''}`.trim(),
        details: changeLines(c),
        tone: 'info',
      };
    case 'staff.roles_change':
      return {
        title: `Changed roles of ${e.subject ?? 'a user'}`,
        details: changeLines(c),
        tone: 'info',
      };
    case 'staff.invite_accept':
      return { title: `${e.subject ?? 'A user'} joined by invitation`, details: [], tone: 'info' };
    case 'staff.activate':
      return { title: `Reactivated ${e.subject ?? 'a user'}`, details: [], tone: 'info' };
    case 'staff.deactivate':
      return { title: `Deactivated ${e.subject ?? 'a user'}`, details: [], tone: 'warning' };
    case 'staff.password_reset':
      return {
        title: `Reset the password of ${e.subject ?? 'a user'}`,
        details: [],
        tone: 'warning',
      };
    case 'staff.sessions_revoke':
      return { title: `Signed ${e.subject ?? 'a user'} out everywhere`, details: [], tone: 'info' };
    case 'staff.invite':
      return { title: 'Invited a user', details: changeLines(c), tone: 'info' };
    case 'staff.invite_resend':
      return { title: 'Invitation sent again', details: [], tone: 'info' };
    case 'staff.invite_revoke':
      return { title: 'Invitation withdrawn', details: changeLines(c), tone: 'info' };
    case 'auth.password_change':
      return { title: 'Changed their own password', details: [], tone: 'info' };
    default:
      return { title: e.action.replace(/[._]/g, ' '), details: changeLines(c), tone: 'info' };
  }
}

const HIDDEN_KEYS = new Set(['id', 'version', 'tenantId', 'createdAt', 'updatedAt', 'ownerUserId']);
const MAX_LINES = 8;

function label(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.]/g, ' ')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function show(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string') return /^\d{4}-\d{2}-\d{2}T/.test(v) ? dateTime(v) : v;
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v)) {
    const items = v.map((x) => (obj(x) ? (str(obj(x)?.name) ?? JSON.stringify(x)) : show(x)));
    return items.length ? items.join(', ') : '—';
  }
  const o = obj(v);
  return o ? (str(o.name) ?? JSON.stringify(o)) : JSON.stringify(v);
}

/**
 * What changed, one line per field: "Name: Ali Traders → Ali & Sons" for updates with before/after,
 * "Name: Ali Traders" for creates.
 */
export function changeLines(c: Changes): string[] {
  if (!c) return [];
  const before = obj(c.before);
  const after = obj(c.after);
  if (before && after) {
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    return keys
      .filter((k) => !HIDDEN_KEYS.has(k) && JSON.stringify(before[k]) !== JSON.stringify(after[k]))
      .slice(0, MAX_LINES)
      .map((k) => `${label(k)}: ${show(before[k])} → ${show(after[k])}`);
  }
  const fields = after ?? before ?? c;
  return Object.entries(fields)
    .filter(([k, v]) => !HIDDEN_KEYS.has(k) && v !== null && v !== undefined && v !== '')
    .slice(0, MAX_LINES)
    .map(([k, v]) => `${label(k)}: ${show(v)}`);
}

type Tone = 'brand' | 'success' | 'info' | 'warning' | 'error';
const TONE: Record<Tone, string> = {
  brand: 'primary.main',
  success: 'success.main',
  info: 'info.main',
  warning: 'warning.main',
  error: 'error.main',
};

const ACTOR_LABEL: Record<Entry['actor']['type'], string> = {
  PLATFORM_ADMIN: 'PackBytes admin',
  USER: 'Shop user',
  SYSTEM: 'Automatic',
};

function ActorAvatar({ actor }: { actor: Entry['actor'] }) {
  const system = actor.type === 'SYSTEM';
  return (
    <Avatar
      sx={(t) => ({
        width: 34,
        height: 34,
        fontSize: 13,
        fontWeight: 700,
        bgcolor: system
          ? (t.vars ?? t).palette.action.selected
          : actor.type === 'PLATFORM_ADMIN'
            ? (t.vars ?? t).palette.primary.main
            : (t.vars ?? t).palette.info.main,
        color: system ? 'text.secondary' : '#fff',
      })}
    >
      {system ? <AccessTimeRounded sx={{ fontSize: 18 }} /> : initials(actor.name)}
    </Avatar>
  );
}

function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return 'Today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return 'Yesterday';
  return date(iso);
}

function Item({ e }: { e: Entry }) {
  const { title, details, tone } = describe(e);
  const shown = details.filter((d): d is string => Boolean(d));
  return (
    <Stack direction="row" spacing={1.5} component="li" sx={{ py: 1.25 }}>
      <ActorAvatar actor={e.actor} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'baseline', flexWrap: 'wrap', rowGap: 0.25 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {e.actor.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {ACTOR_LABEL[e.actor.type]}
          </Typography>
          <Tooltip title={dateTime(e.at)}>
            <Typography variant="caption" color="text.secondary" className="tabular">
              · {relativeTime(e.at)}
            </Typography>
          </Tooltip>
          {e.outcome !== 'SUCCESS' && (
            <Chip size="small" color="error" variant="outlined" label={e.outcome.toLowerCase()} />
          )}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.25 }}>
          <Box
            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TONE[tone], flexShrink: 0 }}
          />
          <Typography variant="body2">{title}</Typography>
        </Stack>
        {shown.length > 0 && (
          <Box
            component="ul"
            sx={(t) => ({
              listStyle: 'none',
              m: 0,
              mt: 0.5,
              ml: 0.5,
              pl: 1.5,
              borderLeft: `2px solid ${(t.vars ?? t).palette.divider}`,
            })}
          >
            {shown.map((line, i) => (
              <Typography
                key={i}
                variant="caption"
                color="text.secondary"
                component="li"
                sx={{ display: 'block', wordBreak: 'break-word' }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Stack>
  );
}

function Day({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box component="section" aria-label={label}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', my: 1 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Stack>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {children}
      </Box>
    </Box>
  );
}

/**
 * Chatter-style history of one shop, newest first: what happened from registration to now and who
 * did it (PackBytes admin, shop user or automatic). Loads older entries on demand.
 */
export function ActivityTimeline({ tenantId }: { tenantId: string }) {
  const q = useInfiniteQuery({
    queryKey: timelineKey(tenantId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET('/platform/tenants/{id}/timeline', {
          params: {
            path: { id: tenantId },
            query: { limit: PAGE, ...(pageParam ? { before: pageParam } : {}) },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });

  const entries = q.data?.pages.flatMap((p) => p.data) ?? [];
  const days: { label: string; items: Entry[] }[] = [];
  for (const e of entries) {
    const label = dayLabel(e.at);
    const last = days.at(-1);
    if (last?.label === label) last.items.push(e);
    else days.push({ label, items: [e] });
  }

  return (
    <Card component="section" aria-label="Activity" sx={{ mt: 3, p: { xs: 2, sm: 2.5 } }}>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="h5">Activity</Typography>
        <Typography variant="caption" color="text.secondary">
          Everything that happened to this shop, and who did it
        </Typography>
      </Stack>
      {q.isPending ? (
        <LoadingRows rows={4} height={52} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} dense />
      ) : entries.length === 0 ? (
        <EmptyState
          dense
          icon={<AccessTimeRounded />}
          title="No activity yet"
          body="Changes to this shop will appear here."
        />
      ) : (
        <>
          {days.map((d) => (
            <Day key={d.label} label={d.label}>
              {d.items.map((e) => (
                <Item key={e.id} e={e} />
              ))}
            </Day>
          ))}
          {q.hasNextPage && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                loading={q.isFetchingNextPage}
                onClick={() => void q.fetchNextPage()}
              >
                Show older activity
              </Button>
            </Box>
          )}
        </>
      )}
    </Card>
  );
}
